import type { ReactNode, Ref } from 'react';
import type { OutlineNode } from '../../../lib/schemaOutline';
import { cn } from '../../../lib/utils';
import { m } from '../../../paraglide/messages';
import { REFERENCE_ID } from '../EndpointReference';

interface ReturnedCardProps {
  /** the list the item sits in (`comments`), or null for a single object */
  listKey: string | null;
  item: Record<string, unknown>;
  /** item keys the endpoint marks as its valuable fields */
  highlighted: string[];
  /** the output schema's direct fields of the item; empty without a schema */
  schemaFields: OutlineNode[];
  mark: ReactNode;
  cardRef?: Ref<HTMLElement>;
  className?: string;
}

/**
 * The most fields the card lists. The card sits over the bottom of the drawn
 * window; at the smallest hero zoom (0.6 at 1024px) the stage is about 600px
 * tall, and eight 24px rows plus the caption and braces keep the card under
 * half of it, so the window's top and the ringed item stay in view.
 */
const MAX_ROWS = 8;

const valueText = (value: unknown): string =>
  value !== null && typeof value === 'object' ? (Array.isArray(value) ? '[...]' : '{...}') : JSON.stringify(value);

interface Row {
  key: string;
  text: string;
  /** true when `text` is the schema's type, for a field the sample lacks */
  typed: boolean;
}

/**
 * The item's rows: the sample's fields with their real values, tinted ones
 * first so a long item never pushes them out of the card, then the schema's
 * fields the sample lacks, stated by type. A sample is a short illustration, so
 * listing it alone made a 72-field profile look like 6 fields.
 */
const itemRows = (item: Record<string, unknown>, highlighted: string[], schemaFields: OutlineNode[]): Row[] => [
  ...Object.entries(item)
    .sort(([a], [b]) => Number(highlighted.includes(b)) - Number(highlighted.includes(a)))
    .map(([key, value]) => ({ key, text: valueText(value), typed: false })),
  ...schemaFields
    .filter((field) => !(field.name in item))
    // The same `| null` label the response schema view and its TypeScript use.
    .map((field) => ({ key: field.name, text: field.nullable ? `${field.type} | null` : field.type, typed: true })),
];

/** What the API returned for the comment the screenshot rings: the sample's
 *  item as JSON, its valuable fields tinted, then a "..." row counting the
 *  fields that follow and linking to the full list in the Reference band. */
export const ReturnedCard = ({ listKey, item, highlighted, schemaFields, mark, cardRef, className }: ReturnedCardProps) => {
  const all = itemRows(item, highlighted, schemaFields);
  const entries = all.slice(0, MAX_ROWS);
  const hidden = all.length - entries.length;
  return (
    <figure ref={cardRef} className={cn('overflow-hidden rounded-12 border border-rule bg-surface shadow-lg', className)}>
      <figcaption className="flex items-center gap-2 border-b border-rule px-4 py-2.5 text-12 text-text-2">
        {mark}
        <span>{m.endpoint_returned_caption()}</span>
        {listKey ? <span className="mono ml-auto text-text-3">{listKey}[0]</span> : null}
      </figcaption>
      <pre className="mono overflow-hidden px-2 py-2.5 text-12 leading-6">
        <div className="px-2 text-text-3">{'{'}</div>
        {entries.map(({ key, text, typed }, i) => (
          <div
            key={key}
            className={cn('flex rounded-6 px-2 pl-6', highlighted.includes(key) && 'bg-accent-soft')}
          >
            <span className={cn('shrink-0', highlighted.includes(key) ? 'text-accent-text' : 'text-text-3')}>
              "{key}":&nbsp;
            </span>
            <span className={cn('min-w-0 truncate', typed ? 'text-text-3' : 'text-text')}>
              {text}
              {i < all.length - 1 ? ',' : ''}
            </span>
          </div>
        ))}
        {hidden > 0 ? (
          <a href={`#${REFERENCE_ID}`} className="block px-2 pl-6 text-text-3 hover:text-accent">
            {(hidden === 1 ? m.endpoint_returned_more_one : m.endpoint_returned_more_many)({ count: hidden })}
          </a>
        ) : null}
        <div className="px-2 text-text-3">{'}'}</div>
      </pre>
    </figure>
  );
};
